import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import classNames from 'classnames';

import type { RatingDay, RatingHour } from 'types/conditionRating';
import { formatTimestampInTimezone, isTimestampThreeHourly } from 'utils/time';

import styles from './GraphTimeline.module.scss';

export enum TimeTickColor {
  LIGHT,
  DARK,
}

interface Props {
  customDateFormat?: string;
  day: RatingDay | null;
  showMarkers?: boolean;
  alignStartOnMidnight?: boolean;
  timeTickColor?: TimeTickColor;
  alignLastLabel?: boolean;
  requiresPadding?: boolean;
}

const GraphTimeline: React.FC<Props> = ({
  customDateFormat = null,
  day,
  showMarkers = false,
  alignStartOnMidnight = false,
  timeTickColor = TimeTickColor.LIGHT,
  alignLastLabel = false,
  requiresPadding = true,
}) => {
  const hourClases = useMemo(
    () =>
      classNames(styles.hour, 'graph-timeline-hour', {
        [styles['hour--showMarker']]: showMarkers,
        [styles.alignStartOnMidnight]: alignStartOnMidnight,
        [styles.darkerTimeTicks]: timeTickColor === TimeTickColor.DARK,
      }),
    [alignStartOnMidnight, showMarkers, timeTickColor],
  );

  const timelineTicks = useMemo(
    () =>
      day?.map((hour: RatingHour, index) => {
        const formattedDate = () => {
          if (!hour?.timestamp) return null;
          const dateFormat = customDateFormat ?? 'h';
          return formatTimestampInTimezone(hour.timestamp, hour.utcOffset, dateFormat).replace(
            '12pm',
            'Noon',
          );
        };

        if (
          !isTimestampThreeHourly(hour?.timestamp ?? null, hour?.utcOffset ?? null) ||
          (index === 0 && alignStartOnMidnight)
        ) {
          return (
            <div
              key={`${hour?.timestamp}${hour?.utcOffset}`}
              className={hourClases}
              data-testid="graph-timeline-hour"
            />
          );
        }

        return (
          <div
            key={`${hour?.timestamp}${hour?.utcOffset}`}
            className={hourClases}
            data-testid="graph-timeline-hour"
          >
            <Typography variant="caption2" color="textSecondary">
              {formattedDate()}
            </Typography>
          </div>
        );
      }),
    [alignStartOnMidnight, customDateFormat, day, hourClases],
  );

  return (
    <Stack
      direction="row"
      className={classNames(styles.wrapper, { [styles.requiresPadding]: requiresPadding })}
      data-testid="graph-timeline"
    >
      <div
        data-testid="graph-timeline-day"
        className={classNames(styles.day, { [styles.alignLastLabel]: alignLastLabel })}
        style={{ width: '100%' }}
      >
        {timelineTicks}
      </div>
    </Stack>
  );
};

export default React.memo(GraphTimeline);
